// Generated by AI on 06/29/2026.
import React, {
    createContext, useState, useMemo, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';

// Modified by AI on 07/07/2026. Edit #2.
// Provide safe defaults so consumers outside the provider don't crash on destructuring
const CandidatesAtsSyncContext = createContext({
    candidatesStatusCache: new Map(),
    startSyncStatusPoll: () => {},
    stopSyncStatusPoll: () => {},
    subscribeToStartPoll: () => () => {},
    subscribeToStopPoll: () => () => {},
});

/**
 * Context provider for managing ATS candidate sync status polling.
 * Replaces RxJS-based CandidatesAtsSyncStore with React Context + useState.
 *
 * Uses ref-backed pub/sub pattern so that emitting a start/stop signal
 * only re-renders the specific SyncCandidateIcon whose talentId matches,
 * rather than re-rendering all consumers (matching old RxJS filter behavior).
 *
 * Provides:
 * - candidatesStatusCache: Map for caching candidate sync status
 * - startSyncStatusPoll: function to emit polling start signal ({ talentId })
 * - stopSyncStatusPoll: function to emit polling stop signal ({ talentId, isListView })
 * - subscribeToStartPoll: subscribe to start poll events, returns unsubscribe fn
 * - subscribeToStopPoll: subscribe to stop poll events, returns unsubscribe fn
 */
// Modified by AI on 07/07/2026. Edit #2.
export const AppWithCandidatesAtsSync = ({ children }) => {
    // Cache Map (preserved from original store)
    const [candidatesStatusCache] = useState(() => new Map());

    // Ref-backed subscriber sets (no state changes on emit = no re-renders)
    const startPollSubscribers = useRef(new Set());
    const stopPollSubscribers = useRef(new Set());

    const subscribeToStartPoll = useCallback((callback) => {
        startPollSubscribers.current.add(callback);
        return () => startPollSubscribers.current.delete(callback);
    }, []);

    const subscribeToStopPoll = useCallback((callback) => {
        stopPollSubscribers.current.add(callback);
        return () => stopPollSubscribers.current.delete(callback);
    }, []);

    const startSyncStatusPoll = useCallback(({ talentId }) => {
        startPollSubscribers.current.forEach((cb) => cb({ talentId }));
    }, []);

    const stopSyncStatusPoll = useCallback(({ talentId, isListView }) => {
        stopPollSubscribers.current.forEach((cb) => cb({ talentId, isListView }));
    }, []);

    const contextValue = useMemo(() => ({
        candidatesStatusCache,
        startSyncStatusPoll,
        stopSyncStatusPoll,
        subscribeToStartPoll,
        subscribeToStopPoll,
    }), [
        candidatesStatusCache,
        startSyncStatusPoll,
        stopSyncStatusPoll,
        subscribeToStartPoll,
        subscribeToStopPoll,
    ]);

    return (
        <CandidatesAtsSyncContext.Provider value={contextValue}>
            {children}
        </CandidatesAtsSyncContext.Provider>
    );
};

AppWithCandidatesAtsSync.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CandidatesAtsSyncContext;
